import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Target } from "lucide-react";
import { KnowledgeScoreIndicator } from "./KnowledgeScoreIndicator";
import { CampaignKnowledgeDetails } from "./CampaignKnowledgeDetails";
import type { CampaignKnowledge } from "./types";
import { formatDistanceToNow } from "date-fns";

interface CampaignKnowledgeDetailsDialogProps {
  knowledgeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guestToken?: string;
}

export function CampaignKnowledgeDetailsDialog({ 
  knowledgeId, 
  open, 
  onOpenChange,
  guestToken 
}: CampaignKnowledgeDetailsDialogProps) {
  const { data: knowledge, isLoading } = useQuery({
    queryKey: ["campaign-knowledge-detail", knowledgeId, guestToken],
    queryFn: async () => {
      // Use edge function for fetching - works for both guest and authenticated users
      const { data, error } = await supabase.functions.invoke("ai-knowledge-assistant", {
        body: {
          action: "fetch",
          type: "campaign",
          knowledgeId,
          guestToken,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data as CampaignKnowledge;
    },
    enabled: open && !!knowledgeId,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500/20 to-green-500/20 border border-emerald-500/20">
                <Target className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <DialogTitle className="text-lg">
                  {isLoading ? <Skeleton className="h-6 w-32" /> : (knowledge?.title || "Campaign Strategy")}
                </DialogTitle>
                {knowledge && (
                  <p className="text-xs text-muted-foreground">
                    Updated {formatDistanceToNow(new Date(knowledge.updated_at), { addSuffix: true })}
                  </p>
                )}
              </div>
            </div>
            {knowledge && (
              <KnowledgeScoreIndicator score={knowledge.knowledge_score} variant="ring" size="md" />
            )}
          </div>
        </DialogHeader>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Loading...</div>
          </div>
        ) : knowledge ? (
          <div className="flex-1 overflow-auto p-6">
            <CampaignKnowledgeDetails knowledge={knowledge} />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
